import React, { createContext, useContext, useState, useEffect } from "react";

const translations = {
  it: {
    nav: {
      home: "Home",
      services: "Servizi",
      properties: "Immobili",
      about: "Studio",
      news: "News",
      shop: "Servizi Online",
      contact: "Contatti",
      cta: "Richiedi consulenza",
    },
    hero: {
      overline: "Studio multiservizi immobiliare e patrimoniale",
      title: "Patrimoni che",
      titleAccent: "generano valore.",
      subtitle:
        "Da oltre vent'anni affianchiamo famiglie, professionisti e investitori nella gestione, valorizzazione e crescita del patrimonio. Un unico interlocutore per immobiliare, finanza, fiscalità e consulenza commerciale: competenze integrate al servizio dei tuoi obiettivi di lungo periodo.",
      ctaPrimary: "Esplora i servizi",
      ctaSecondary: "Vetrina immobili",
      stat1: "Anni di esperienza",
      stat2: "Operazioni concluse",
      stat3: "Rendimento medio",
    },
    about: {
      overline: "Lo studio",
      title: "Una visione integrata del patrimonio.",
      paragraph1:
        "Makelaars360 nasce dall'esigenza di superare la logica del professionista isolato: troppo spesso il cliente si ritrova a coordinare in autonomia agente immobiliare, commercialista, notaio, banca e consulente assicurativo, perdendo tempo, opportunità e — non di rado — denaro. Noi abbiamo riunito tutte queste competenze sotto un unico tetto.",
      paragraph2:
        "Il risultato è uno studio multiservizi che dialoga in modo nativo con ogni esigenza patrimoniale: dall'acquisto della prima casa alla strutturazione di un portafoglio di investimento, dall'apertura di un'attività commerciale alla protezione assicurativa del nucleo familiare. Un solo riferimento, una sola strategia, un solo interesse: il tuo.",
      paragraph3:
        "Il nostro approccio è sartoriale. Ogni cliente è seguito da un team multidisciplinare che integra valutazione, fiscalità, ristrutturazione, contabilità, copertura assicurativa e marketing immobiliare in un percorso unico, condiviso e misurabile. Niente formule preconfezionate: solo soluzioni costruite sul tuo specifico contesto patrimoniale, fiscale e di vita.",
      bullet1: "Approccio multidisciplinare integrato",
      bullet2: "Network di professionisti certificati",
      bullet3: "Trasparenza e governance del processo",
      bullet4: "Reportistica chiara e periodica",
    },
    services: {
      overline: "I nostri servizi",
      title: "Un servizio dedicato per ogni esigenza.",
      subtitle:
        "Nove aree di specializzazione che lavorano in sinergia per la gestione completa del tuo patrimonio. Ogni servizio è erogato direttamente dallo studio o tramite partner interni qualificati, sempre sotto un'unica regia strategica.",
      items: {
        wealth: {
          title: "Gestione Patrimoniale",
          desc: "Pianificazione strategica del patrimonio familiare e d'impresa: asset allocation, diversificazione, passaggio generazionale e protezione del capitale nel lungo periodo. Definiamo insieme obiettivi, orizzonte temporale e tolleranza al rischio per costruire un percorso davvero su misura.",
        },
        sale: {
          title: "Compravendita",
          desc: "Selezione, valutazione e negoziazione di immobili residenziali e commerciali. Ti accompagniamo dalla ricerca della proprietà al rogito notarile, con due diligence documentale, verifiche urbanistiche e supporto fiscale. Massima trasparenza, zero sorprese.",
        },
        invest: {
          title: "Investimento Immobiliare",
          desc: "Identificazione di opportunità ad alto rendimento, due diligence tecnica e finanziaria, strutturazione dell'operazione e business plan. Lavoriamo su residenziale, commerciale, frazionamenti e operazioni di flip, sempre con metriche di rendimento misurabili.",
        },
        auctions: {
          title: "Aste Immobiliari & Sovraindebitamento",
          desc: "Assistenza completa nella partecipazione ad aste giudiziarie: lettura della perizia, sopralluogo tecnico, gestione cauzione, partecipazione telematica e saldo prezzo. Affianchiamo inoltre chi rischia di perdere la propria casa o si trova in stato di sovraindebitamento, grazie alla collaborazione con avvocati in sede: piani di ristrutturazione del debito, esdebitazione e tutela del nucleo familiare.",
        },
        consulting: {
          title: "Apertura Attività & Somministrazione",
          desc: "Consulenza chiavi in mano per l'apertura di bar, ristoranti, hotel e attività commerciali: business plan, ricerca della location, SCIA, licenze, requisiti igienico-sanitari e gestione delle pratiche comunali. Riduciamo i tempi medi di apertura del 40%.",
        },
        enhancement: {
          title: "Miglioramento Asset",
          desc: "Home staging professionale, ristrutturazione mirata, efficientamento energetico e riposizionamento sul mercato. Il nostro obiettivo è una sola metrica: massimizzare la rendita o il valore di rivendita del tuo immobile, con investimenti contenuti e ROI verificabile.",
        },
        valorization: {
          title: "Valorizzazione Immobili",
          desc: "Trasformazione di immobili sottoutilizzati in asset ad alto rendimento: cambio destinazione d'uso, frazionamenti, riconversioni residenziale-turistico, sviluppi RTA e short rental. Studi di fattibilità, pratiche edilizie e gestione dei lavori inclusi.",
        },
        insurance: {
          title: "Consulenza Assicurativa & Contabile",
          desc: "Tramite partner interni qualificati offriamo protezione assicurativa (polizze immobiliari, RC, vita, key-man, tutela legale) e servizi contabili e fiscali completi: contabilità ordinaria e semplificata, dichiarativi, pianificazione tributaria e supporto in operazioni straordinarie.",
        },
        interior: {
          title: "Interior Design & Ristrutturazione",
          desc: "Progettazione di interni e ristrutturazioni chiavi in mano per spazi residenziali e commerciali. Dal concept al rendering 3D, dalle pratiche edilizie alla direzione lavori: arredi su misura, materiali ricercati, gestione completa del cantiere. Tempi certificati, costi trasparenti e un risultato che esprime identità e valorizza l'immobile.",
        },
      },
    },
    properties: {
      overline: "Vetrina",
      title: "Immobili selezionati per te.",
      subtitle:
        "Una selezione curata di proprietà di pregio e opportunità di investimento, frutto del lavoro quotidiano del nostro team. Ogni immobile è stato visitato, valutato e validato dai nostri professionisti.",
      viewAll: "Esplora tutta la vetrina",
      viewDetails: "Scopri di più",
      filters: {
        all: "Tutti",
        residential: "Residenziale",
        luxury: "Luxury",
        commercial: "Commerciale",
        investment: "Investimento",
      },
      details: {
        bedrooms: "Camere",
        bathrooms: "Bagni",
        area: "Superficie",
        location: "Località",
        price: "Prezzo",
        contact: "Richiedi informazioni",
        back: "Torna alla vetrina",
        description: "Descrizione",
      },
    },
    news: {
      overline: "Insights",
      title: "Approfondimenti dal nostro studio.",
      subtitle:
        "Mercato, normativa, casi studio operativi. Le analisi del nostro team — sempre concrete, sempre verificate sul campo — a tua disposizione per decidere meglio.",
      readMore: "Continua a leggere",
      viewAll: "Tutti gli articoli",
      back: "Torna alle news",
      by: "di",
    },
    interiorHighlight: {
      overline: "Servizio in evidenza",
      title: "Interior Design & Ristrutturazione",
      tagline: "Lo spazio che racconta chi sei.",
      paragraph:
        "Trasformiamo case, uffici e attività commerciali in ambienti che esprimono identità, funzionalità e valore. Dal primo concept alla consegna chiavi in mano, ogni progetto è coordinato da un unico project manager e da un team di interior designer, architetti e maestranze selezionate.",
      bullet1: "Progettazione concept, mood board e rendering 3D",
      bullet2: "Selezione di materiali, finiture e arredi su misura",
      bullet3: "Pratiche edilizie e direzione lavori incluse",
      bullet4: "Tempi e costi certificati a contratto",
      cta: "Richiedi un preventivo",
      stat1Label: "Progetti realizzati",
      stat1Value: "30+",
      stat2Label: "Tempi medi consegna",
      stat2Value: "8 sett.",
      stat3Label: "Soddisfazione clienti",
      stat3Value: "100%",
    },
    contact: {
      overline: "Contatti",
      title: "Iniziamo a costruire valore.",
      subtitle:
        "Compila il form per richiedere una consulenza riservata e gratuita. Ti risponderemo entro 24 ore lavorative con una prima analisi del tuo caso e con la proposta del professionista più indicato del nostro studio.",
      name: "Nome e cognome",
      email: "Email",
      phone: "Telefono",
      service: "Servizio di interesse",
      selectService: "Seleziona un servizio",
      subject: "Oggetto",
      message: "Messaggio",
      submit: "Invia richiesta",
      sending: "Invio in corso...",
      success: "Grazie! Ti contatteremo a breve.",
      error: "Si è verificato un errore. Riprova.",
      office: "Ufficio",
      hours: "Orari",
      hoursValue: "Lun–Ven 9:00 — 19:00",
      phoneLabel: "Telefono",
      emailLabel: "Email",
    },
    footer: {
      tagline:
        "Studio multiservizi immobiliare e patrimoniale. Patrimoni che generano valore, sotto un'unica regia strategica.",
      services: "Servizi",
      company: "Studio",
      legal: "Note legali",
      privacy: "Privacy Policy",
      cookies: "Cookie Policy",
      terms: "Termini",
      rights: "Tutti i diritti riservati.",
    },
    shop: {
      overline: "Servizi online",
      title: "Consulenze e pacchetti acquistabili online.",
      subtitle:
        "Soluzioni concrete a prezzi trasparenti. Acquista, ricevi conferma e ti contattiamo entro 24h per pianificare la consulenza.",
      previewTitle: "Servizi rapidi a prezzo fisso",
      previewSubtitle:
        "Consulenze, audit e pacchetti ready-to-go. Acquista online in modo semplice e sicuro.",
      viewAll: "Scopri tutti i pacchetti",
      categoryEntry: "Consulenze rapide",
      categoryPremium: "Pacchetti Premium",
      categoryMembership: "Abbonamento",
      categoryEntryDesc:
        "Sessioni consulenziali e analisi rapide per chi vuole capire come muoversi.",
      categoryPremiumDesc:
        "Servizi creativi e operativi per case, attività ricettive e locali commerciali.",
      categoryMembershipDesc:
        "Affiancamento continuativo per investitori e proprietari attivi.",
      buyNow: "Acquista ora",
      from: "da",
      promoBadge: "Promo lancio",
      bestseller: "Più richiesto",
      monthly: "/mese",
      starting: "a partire da",
      includes: "Cosa include",
      duration: "Durata",
      deliverable: "Cosa ricevi",
      processing: "Elaborazione...",
      backToShop: "Torna ai pacchetti",
      successTitle: "Pagamento ricevuto",
      successMessage:
        "Grazie per il tuo acquisto! Ti contatteremo entro 24 ore lavorative all'email indicata per pianificare il servizio.",
      pendingTitle: "Pagamento in elaborazione",
      pendingMessage: "Stiamo verificando il pagamento. Attendi qualche secondo...",
      errorTitle: "Pagamento non completato",
      errorMessage: "Il pagamento non è andato a buon fine. Puoi riprovare quando vuoi.",
      orderRef: "Riferimento ordine",
      backHome: "Torna alla home",
      packages: {
        "sos-casa": {
          title: "SOS Casa & Investimento",
          tagline: "Hai dubbi? Ti aiutiamo a fare chiarezza.",
          desc: "Pacchetto pensato per chi è confuso e vuole capire se comprare, partecipare a un'asta, fare Airbnb, ristrutturare o se un immobile rende davvero. Una sessione operativa con i nostri esperti per portarti dalla domanda alla decisione.",
          duration: "Videocall di 60 minuti",
          features: [
            "Videocall 1:1 di 60 minuti con un nostro consulente",
            "Analisi completa della tua situazione",
            "Consigli pratici e personalizzati",
            "Mini piano operativo a passi concreti",
            "Checklist PDF da consultare nel tempo",
          ],
        },
        "airbnb-check": {
          title: "Airbnb Profit Check",
          tagline: "Il tuo appartamento può rendere di più.",
          desc: "Per chi ha appartamenti vuoti o poco redditizi e vuole capire come trasformarli in fonti di reddito. Analizziamo annunci, foto, arredo, spazi e palette per stimare l'incremento reale di redditività ottenibile.",
          duration: "Deliverable in 5 giorni lavorativi",
          features: [
            "Analisi degli annunci e dei competitor",
            "Suggerimenti di arredo e ottimizzazione spazi",
            "Palette colori e proposta materiali",
            "Consigli foto per aumentare conversion",
            "Stima incremento di redditività",
            "PDF professionale conclusivo",
          ],
        },
        "pre-asta": {
          title: "Pre-Asta Sicura",
          tagline: "Prima di rilanciare, sai davvero cosa stai comprando?",
          desc: "Verifica completa prima di partecipare a un'asta giudiziaria: analisi documentale, valore reale, rischi e strategia. Un investimento minimo per evitare un errore da decine di migliaia di euro.",
          duration: "Report in 7 giorni",
          features: [
            "Verifica documenti e perizia tecnica",
            "Analisi rischi (occupazione, vincoli, debiti)",
            "Stima valore di mercato e prezzo congruo",
            "Strategia di partecipazione e cap massimo",
            "Stima costi reali post-aggiudicazione",
          ],
        },
        relooking: {
          title: "Relooking Express",
          tagline: "Trasforma lo spazio. In settimane, non in mesi.",
          desc: "Restyling rapido per negozi, B&B, hotel boutique e appartamenti Airbnb. Concept, mood board e shopping list pronti all'uso, con render AI per visualizzare il risultato finale prima di iniziare.",
          duration: "Consegna in 3-4 settimane",
          features: [
            "Mood board e concept estetico",
            "Disposizione arredi e space planning",
            "Palette colori e finiture",
            "Shopping list completa con link",
            "Render AI fotorealistici",
          ],
        },
        "airbnb-ready": {
          title: "Airbnb Ready",
          tagline: "Dal vuoto al pronto-ospite.",
          desc: "Pacchetto chiavi in mano per trasformare un appartamento in struttura ricettiva di successo. Concept, target ospiti, arredo, ottimizzazione budget e check-in experience: ogni dettaglio curato per massimizzare prenotazioni e recensioni.",
          duration: "Consegna in 8-12 settimane",
          features: [
            "Concept completo e identità visiva",
            "Strategia target ospiti e positioning",
            "Arredo e selezione materiali",
            "Ottimizzazione budget e ROI",
            "Check-in experience e welcome kit",
            "Lista acquisti dettagliata",
          ],
        },
        investitore: {
          title: "Investitore 360",
          tagline: "Il tuo studio multidisciplinare a portata di chat.",
          desc: "Affiancamento continuativo mensile per investitori, proprietari attivi e professionisti che vogliono un partner sempre presente. Tre livelli per adattarsi alle tue esigenze.",
          duration: "Membership mensile",
          features: [
            "1 call al mese con il tuo consulente dedicato",
            "Supporto WhatsApp prioritario nei giorni lavorativi",
            "Segnalazioni di opportunità di investimento riservate",
            "Mini-consulenze su contratti, perizie, scelte strategiche",
            "Priorità in agenda per appuntamenti urgenti",
          ],
          tiers: {
            base: { label: "Base", price: 49 },
            plus: { label: "Plus", price: 99 },
            premium: { label: "Premium", price: 199 },
          },
        },
      },
    },
  },
  en: {
    nav: {
      home: "Home",
      services: "Services",
      properties: "Properties",
      about: "Studio",
      news: "Insights",
      shop: "Online Services",
      contact: "Contact",
      cta: "Book consultation",
    },
    hero: {
      overline: "Multiservice real estate & wealth studio",
      title: "Assets that",
      titleAccent: "generate value.",
      subtitle:
        "For over twenty years we have supported families, professionals and investors in managing, enhancing and growing their wealth. One single point of contact for real estate, finance, taxation and business consulting: integrated expertise serving your long-term goals.",
      ctaPrimary: "Explore services",
      ctaSecondary: "View properties",
      stat1: "Years of experience",
      stat2: "Closed transactions",
      stat3: "Average yield",
    },
    about: {
      overline: "The studio",
      title: "An integrated view of the patrimony.",
      paragraph1:
        "Makelaars360 was born to overcome the logic of the isolated professional: too often the client ends up coordinating real estate agent, accountant, notary, bank and insurance advisor on their own, losing time, opportunities and — not rarely — money. We have brought all these skills under one roof.",
      paragraph2:
        "The result is a multiservice studio that natively addresses every wealth-related need: from buying a first home to structuring an investment portfolio, from opening a commercial activity to insuring the family. One single reference, one strategy, one interest: yours.",
      paragraph3:
        "Our approach is bespoke. Every client is supported by a multidisciplinary team that integrates valuation, taxation, renovation, accounting, insurance coverage and real estate marketing into a single, shared and measurable path. No prepackaged formulas: only solutions built on your specific patrimonial, fiscal and life context.",
      bullet1: "Multidisciplinary, integrated approach",
      bullet2: "Network of certified professionals",
      bullet3: "Transparency and process governance",
      bullet4: "Clear and periodic reporting",
    },
    services: {
      overline: "Our services",
      title: "A dedicated service for every need.",
      subtitle:
        "Nine specialization areas working in synergy for the full management of your assets. Each service is delivered directly by the studio or through qualified internal partners, always under one strategic direction.",
      items: {
        wealth: {
          title: "Wealth Management",
          desc: "Strategic planning of family and corporate assets: asset allocation, diversification, generational transfer and long-term capital protection. We define together your goals, time horizon and risk tolerance to build a truly tailored path.",
        },
        sale: {
          title: "Buying & Selling",
          desc: "Selection, appraisal and negotiation of residential and commercial properties. We support you from search to notarial deed, with documentary due diligence, urban planning checks and tax assistance. Maximum transparency, zero surprises.",
        },
        invest: {
          title: "Real Estate Investment",
          desc: "Identification of high-yield opportunities, technical and financial due diligence, deal structuring and business planning. We work on residential, commercial, splits and flips, always with measurable yield metrics.",
        },
        auctions: {
          title: "Property Auctions & Over-indebtedness",
          desc: "Full assistance in judicial auctions: appraisal review, technical inspection, deposit handling, online participation and final balance. We also support people at risk of losing their home or facing over-indebtedness, in collaboration with in-house lawyers: debt restructuring plans, discharge of debts and protection of the family.",
        },
        consulting: {
          title: "Business Setup & F&B",
          desc: "Turnkey advisory for opening bars, restaurants, hotels and commercial activities: business plan, location scouting, SCIA, licenses, sanitary requirements and municipal procedures. We cut average opening times by 40%.",
        },
        enhancement: {
          title: "Asset Enhancement",
          desc: "Professional home staging, targeted renovation, energy upgrade and market repositioning. Our goal is one single metric: maximize the yield or resale value of your property, with controlled investment and verifiable ROI.",
        },
        valorization: {
          title: "Property Valorization",
          desc: "Transformation of underused properties into high-yield assets: change of use, splits, residential-to-tourist conversions, RTA developments and short rentals. Feasibility studies, building permits and works management included.",
        },
        insurance: {
          title: "Insurance & Accounting Advisory",
          desc: "Through qualified internal partners we provide insurance protection (property, liability, life, key-man, legal expenses) and full accounting and tax services: ordinary and simplified bookkeeping, tax filings, tax planning and support in extraordinary operations.",
        },
        interior: {
          title: "Interior Design & Renovation",
          desc: "Interior design and turnkey renovations for residential and commercial spaces. From concept to 3D rendering, from building permits to works supervision: bespoke furniture, refined materials, full site management. Certified timing, transparent costs and a result that expresses identity and enhances the property.",
        },
      },
    },
    properties: {
      overline: "Showcase",
      title: "Properties curated for you.",
      subtitle:
        "A curated selection of premium properties and investment opportunities, the result of our team's daily work. Every property has been visited, appraised and validated by our professionals.",
      viewAll: "Explore the full showcase",
      viewDetails: "Discover more",
      filters: {
        all: "All",
        residential: "Residential",
        luxury: "Luxury",
        commercial: "Commercial",
        investment: "Investment",
      },
      details: {
        bedrooms: "Bedrooms",
        bathrooms: "Bathrooms",
        area: "Area",
        location: "Location",
        price: "Price",
        contact: "Request information",
        back: "Back to showcase",
        description: "Description",
      },
    },
    news: {
      overline: "Insights",
      title: "Insights from our studio.",
      subtitle:
        "Market, regulation, real-life case studies. Analyses from our team — always concrete, always tested on the field — at your disposal to make better decisions.",
      readMore: "Continue reading",
      viewAll: "All articles",
      back: "Back to news",
      by: "by",
    },
    interiorHighlight: {
      overline: "Featured service",
      title: "Interior Design & Renovation",
      tagline: "The space that tells who you are.",
      paragraph:
        "We transform homes, offices and commercial activities into environments that express identity, functionality and value. From the first concept to turnkey delivery, every project is coordinated by a single project manager and a team of interior designers, architects and selected craftsmen.",
      bullet1: "Concept design, mood board and 3D renderings",
      bullet2: "Selection of materials, finishes and bespoke furniture",
      bullet3: "Building permits and works supervision included",
      bullet4: "Time and costs certified by contract",
      cta: "Request a quote",
      stat1Label: "Completed projects",
      stat1Value: "30+",
      stat2Label: "Average delivery time",
      stat2Value: "8 weeks",
      stat3Label: "Client satisfaction",
      stat3Value: "100%",
    },
    contact: {
      overline: "Contact",
      title: "Let's start building value.",
      subtitle:
        "Fill out the form to request a confidential, free consultation. We will respond within 24 working hours with an initial analysis of your case and the recommendation of the most suitable professional from our studio.",
      name: "Full name",
      email: "Email",
      phone: "Phone",
      service: "Service of interest",
      selectService: "Select a service",
      subject: "Subject",
      message: "Message",
      submit: "Send request",
      sending: "Sending...",
      success: "Thank you! We will contact you shortly.",
      error: "An error occurred. Please try again.",
      office: "Office",
      hours: "Hours",
      hoursValue: "Mon–Fri 9:00 — 19:00",
      phoneLabel: "Phone",
      emailLabel: "Email",
    },
    footer: {
      tagline:
        "Multiservice real estate and wealth studio. Assets that generate value, under one strategic direction.",
      services: "Services",
      company: "Studio",
      legal: "Legal",
      privacy: "Privacy Policy",
      cookies: "Cookie Policy",
      terms: "Terms",
      rights: "All rights reserved.",
    },
    shop: {
      overline: "Online services",
      title: "Consulting and packages you can buy online.",
      subtitle:
        "Concrete solutions at transparent prices. Buy, get confirmation, and we'll contact you within 24 hours to schedule your service.",
      previewTitle: "Quick fixed-price services",
      previewSubtitle:
        "Consultations, audits and ready-to-go packages. Buy online easily and securely.",
      viewAll: "Discover all packages",
      categoryEntry: "Quick consulting",
      categoryPremium: "Premium packages",
      categoryMembership: "Membership",
      categoryEntryDesc:
        "Consulting sessions and quick analyses for those who want to understand how to move.",
      categoryPremiumDesc:
        "Creative and operational services for homes, hospitality and commercial venues.",
      categoryMembershipDesc:
        "Continuous support for active investors and property owners.",
      buyNow: "Buy now",
      from: "from",
      promoBadge: "Launch promo",
      bestseller: "Most requested",
      monthly: "/month",
      starting: "starting from",
      includes: "What's included",
      duration: "Duration",
      deliverable: "What you get",
      processing: "Processing...",
      backToShop: "Back to packages",
      successTitle: "Payment received",
      successMessage:
        "Thank you for your purchase! We'll contact you within 24 working hours at the email provided to plan your service.",
      pendingTitle: "Payment processing",
      pendingMessage: "We're verifying your payment. Please wait a few seconds...",
      errorTitle: "Payment not completed",
      errorMessage: "Your payment was not completed. You can try again whenever you want.",
      orderRef: "Order reference",
      backHome: "Back to home",
      packages: {
        "sos-casa": {
          title: "SOS Home & Investment",
          tagline: "Got doubts? We'll help you see clearly.",
          desc: "Designed for those who are confused and want to understand whether to buy, join an auction, run an Airbnb, renovate or if a property really yields. An operational session with our experts to take you from question to decision.",
          duration: "60-minute video call",
          features: [
            "60-minute 1:1 video call with our consultant",
            "Full analysis of your situation",
            "Practical, personalized advice",
            "Mini operational plan with concrete steps",
            "PDF checklist to keep over time",
          ],
        },
        "airbnb-check": {
          title: "Airbnb Profit Check",
          tagline: "Your apartment can earn more.",
          desc: "For those with empty or low-yield apartments who want to turn them into income sources. We analyze listings, photos, furniture, spaces and palette to estimate the real yield uplift available.",
          duration: "Deliverable in 5 working days",
          features: [
            "Listing and competitor analysis",
            "Furnishing and space optimization advice",
            "Color palette and material proposal",
            "Photography tips to boost conversion",
            "Yield uplift estimate",
            "Final professional PDF",
          ],
        },
        "pre-asta": {
          title: "Safe Pre-Auction",
          tagline: "Before bidding, do you really know what you're buying?",
          desc: "Full verification before joining a judicial auction: documentary analysis, real value, risks and strategy. A small investment to avoid a costly mistake.",
          duration: "Report in 7 days",
          features: [
            "Document and technical appraisal review",
            "Risk analysis (occupancy, restrictions, debts)",
            "Market value and fair-price estimate",
            "Bidding strategy and max cap",
            "Real post-award cost estimate",
          ],
        },
        relooking: {
          title: "Express Relooking",
          tagline: "Transform the space. In weeks, not months.",
          desc: "Quick restyling for shops, B&Bs, boutique hotels and Airbnb apartments. Concept, mood board and shopping list ready to use, with AI renderings to preview the final result before starting.",
          duration: "Delivery in 3-4 weeks",
          features: [
            "Mood board and aesthetic concept",
            "Furniture layout and space planning",
            "Color palette and finishes",
            "Full shopping list with links",
            "Photorealistic AI renderings",
          ],
        },
        "airbnb-ready": {
          title: "Airbnb Ready",
          tagline: "From empty to guest-ready.",
          desc: "Turnkey package to transform an apartment into a successful hospitality venue. Concept, guest target, furniture, budget optimization and check-in experience: every detail crafted to maximize bookings and reviews.",
          duration: "Delivery in 8-12 weeks",
          features: [
            "Full concept and visual identity",
            "Guest target strategy and positioning",
            "Furniture and material selection",
            "Budget optimization and ROI",
            "Check-in experience and welcome kit",
            "Detailed shopping list",
          ],
        },
        investitore: {
          title: "Investor 360",
          tagline: "Your multidisciplinary studio one chat away.",
          desc: "Continuous monthly support for investors, active owners and professionals who want a partner always available. Three tiers to fit your needs.",
          duration: "Monthly membership",
          features: [
            "1 call/month with your dedicated advisor",
            "Priority WhatsApp support on working days",
            "Off-market investment opportunity alerts",
            "Mini consultations on contracts, appraisals, strategy",
            "Priority booking for urgent appointments",
          ],
          tiers: {
            base: { label: "Base", price: 49 },
            plus: { label: "Plus", price: 99 },
            premium: { label: "Premium", price: 199 },
          },
        },
      },
    },
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "it");

  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = translations[lang];
  const toggle = () => setLang((p) => (p === "it" ? "en" : "it"));

  return (
    <I18nContext.Provider value={{ lang, setLang, toggle, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
